import React from 'react';
import RSSLider from 'better-react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { Button } from 'src/client/components/ui/Button';

type ButtonLink = {
  link: string;
  label: string;
};

type Props = {
  title?: React.ReactNode;
  buttons?: Array<React.ReactNode | ButtonLink>;
  children: React.ReactNode;
  settings?: Record<string, unknown>;
  className?: string;
};

type NavButtonProps = {
  direction: 'right' | 'left';
  className?: string;
  onClick?: () => void;
};

const SliderWrapper = styled.div`
  & .slider {
    h3.title {
      font-weight: 300;
      margin-top: 0;
    }
  }

  & .previous {
    right: 30px;
  }

  & .next {
    right: 0;
  }

  & .slick-next,
  & .slick-prev {
    background-image: none;
    background-color: ${(props) => props.theme.primary6};

    :hover {
      background-image: none;
      background-color: ${(props) => props.theme.primary3};
    }
  }

  .slick-disabled {
    background-color: ${(props) => props.theme.primary3};
    cursor: default;
  }
  & .header {
    display: inline-block;
    width: calc(100% - 58px); // accomodate for navigation buttons
  }

  & .title {
    font-weight: 100;
    margin-top: 0;
    min-height: 28px;
    line-height: 28px;
    display: inline-block;
  }

  & .btn {
    line-height: 28px;
    padding: 0;
    top: 0;
    margin: 0 10px;
    display: inline-block;
    height: 28px;
    cursor: pointer;
  }

  & .btn:hover {
    text-decoration: underline;
  }

  & .btn:focus,
  & .btn:active {
    outline: none;
    box-shadow: none;
  }

  & .navigation-button {
    border: 0 none;
    border-radius: 4px;
    height: 24px;
    top: -36px;
    width: 24px;
    z-index: 1;
  }
`;

const NavigationButton = (props: NavButtonProps) => {
  const { direction, className, onClick } = props;
  return (
    <Button
      className={`navigation-button ${className} ${direction === 'left' ? 'previous' : 'next'}`}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={direction === 'left' ? faAngleLeft : faAngleRight} />
    </Button>
  );
};

export const Slider = (props: Props) => {
  const { title, buttons = [], children, className = '', settings = {} } = props;

  const sliderSettings = {
    prevArrow: <NavigationButton direction="left" />,
    nextArrow: <NavigationButton direction="right" />,
    dots: false,
    infinite: false,
    slidesToShow: 6,
    slidesToScroll: 6,
    ...settings,
  };

  const isButtonLink = (button: React.ReactNode | ButtonLink): button is ButtonLink =>
    typeof button === 'object' && button !== null && 'link' in button && 'label' in button;

  const renderButtons = () =>
    buttons.map((button) => {
      if (React.isValidElement(button)) {
        return button;
      }
      if (isButtonLink(button)) {
        return (
          <Link key={button.link} to={button.link} className="pull-right btn clickable">
            {button.label}
          </Link>
        );
      }
      return null;
    });

  const renderTitle = () => (typeof title === 'string' ? <h3 className="title">{title}</h3> : title);
  const renderHeader = title && buttons;

  return (
    <SliderWrapper className={`row container ${className}`}>
      <div className="slider">
        {renderHeader && (
          <div className="header">
            {title && renderTitle()}
            {buttons && renderButtons()}
          </div>
        )}
        <RSSLider {...sliderSettings}>{children}</RSSLider>
      </div>
    </SliderWrapper>
  );
};
